import { useSelector } from 'react-redux'
import { Box, Container, Skeleton, Typography, Paper } from '@mui/material'
import Feeds from './Feeds/Feeds'
import { useEffect, useState } from 'react'
import Wall from './Wall/Wall'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const Content = () => {
  const licenses = useSelector((state) => state?.tenant?.license || [])
  const featureFlags = useSelector((state) => state?.tenant?.featureFlag || {})
  const checkAssessment = licenses.some((license) => license._id === '61fc0f1d6bbfcac1a18f02ba')
  const userProfile = useSelector((state) => state?.session?.profile)
  const userNotifications = useSelector((state) => state?.session?.notifications)
  const permissions = useSelector((state) => state?.session?.permissions?.feed || {})
  const showFeed = userNotifications?.hasFeed && featureFlags?.showFeed
  // MF-546: the Student/Parent/Teacher home is gated by the assessment license, not by the flag —
  // the parent's children list lives in it and must always show (a flagless tenant left parents
  // with a blank home).
  const showHome =
    userProfile?.roles?.some((role) => ['Student', 'Parent', 'Teacher'].includes(role.id)) && checkAssessment
  // MF-559: the dashboard itself (growth journey, skills, teacher widgets) is opt-in per tenant via
  // settings.featureFlag.showDashboard — missing flag = off. Parents keep their children list.
  const dashboardEnabled = featureFlags?.showDashboard === true
  const isParent = userProfile?.roles?.every((role) => role.name === 'Parent')
  const showDashboard = showHome && (dashboardEnabled || isParent)
  // MF-565: the 👋 "Welcome, <name>!" greeting belongs to the dashboard — for Student / Parent / Teacher
  // it follows the same showDashboard flag. Other roles keep it. The Feed/Dashboard toggle stays (a parent
  // with the feed still needs the toggle to reach it). MF-575: the active-term box was removed from the landing.
  const showWelcome = showHome ? dashboardEnabled : true
  const [type, setType] = useState(showDashboard ? 'dashboard' : 'wall')

  useEffect(() => {
    setType(showDashboard ? 'dashboard' : 'wall')
  }, [showDashboard])

  // MF-542 — the student summary dashboard is no longer a home widget behind a feature flag; it's a
  // bi-custom Analytics chart, surfaced/assigned via Analytics Charts (who sees it + default per role).
  return (
    <Container maxWidth='lg' className='flex flex-col gap-4 py-4 h-full'>
      {(showFeed || showHome) && (
        <Box>
          {/* MF-529 — flex-wrap instead of a fixed 12-column grid: the greeting takes the free space
              (and ellipsises) and the Feed/Dashboard toggle sits next to it, wrapping on narrow screens. */}
          <Box className='flex flex-wrap items-center gap-4'>
            {showWelcome && (
              <Paper
                style={{ border: '1px solid #e5e7eb', borderRadius: '8px', flex: '1 1 320px', minWidth: 0 }}
                className='flex items-center gap-3  h-10'
                sx={{ pt: 2, pb: 2, px: 2 }}
                elevation={0}
              >
                <Box
                  sx={{
                    fontSize: '2rem',
                    marginRight: '0.5rem',
                    animation: 'wave 2s ease-in-out 2',
                    transformOrigin: '70% 70%',
                    '@keyframes wave': {
                      '0%, 100%': { transform: 'rotate(0deg)' },
                      '10%, 30%, 50%, 70%': { transform: 'rotate(-10deg) scale(1.1)' },
                      '20%, 40%, 60%': { transform: 'rotate(10deg) scale(1.1)' },
                      '80%': { transform: 'rotate(-5deg) scale(1.05)' },
                      '90%': { transform: 'rotate(5deg) scale(1.05)' }
                    }
                  }}
                >
                  👋
                </Box>
                <Typography variant='h4' component='span' sx={{ fontWeight: 400, marginRight: '0.5rem' }}>
                  Welcome,
                </Typography>
                <Typography
                  variant='h4'
                  color='primary'
                  fontWeight='bold'
                  component='span'
                  className='text-nowrap text-overflow-hidden overflow-hidden text-ellipsis'
                  sx={{ minWidth: 0 }}
                >
                  {userProfile ? (
                    `${userProfile?.fullName || userProfile?.code}!`
                  ) : (
                    <Skeleton width={200} height={40} />
                  )}
                </Typography>
              </Paper>
            )}
            {showFeed && showDashboard && (
              <Paper
                className='h-10 flex flex-col items-center justify-center cursor-pointer  bg-primary text-primary-foreground'
                sx={{ pt: 2, pb: 2, px: 2, flex: '0 0 auto', minWidth: 'calc((100% - 11rem) / 12 - 32px)' }}
                onClick={() => (type === 'dashboard' ? setType('wall') : setType('dashboard'))}
              >
                <FontAwesomeIcon icon={type === 'dashboard' ? 'rss' : 'chart-line'} size='2x' />
                <Typography component='span' sx={{ fontWeight: 400 }}>
                  {type === 'dashboard' ? 'Feed' : 'Dashboard'}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      )}
      {showDashboard && type === 'dashboard' && (
        <Wall
          user={userProfile}
          checkAssessment={checkAssessment}
          featureFlags={featureFlags}
          dashboardEnabled={dashboardEnabled}
        />
      )}
      {showFeed && type === 'wall' && (
        <Feeds userData={userProfile} featureFlags={featureFlags} permissions={permissions} />
      )}
    </Container>
  )
}

export default Content
